import { useEffect, useMemo } from "react";
import {
  faEdit,
  faTrashAlt,
  faBuilding,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router-dom";
import ConfirmationModal from "../../../components/modals/ConfirmationModal";
import "../../../assets/styles/custom-styles.css";
import MainLayout from "../../../layouts/MainLayout";
import { usePermissions } from "../../../hooks/usePermissions";
import useLoader from "../../../hooks/useLoader";
import useNotification from "../../../hooks/useNotification";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import ListHeader from "../../../components/ListHeader";
import useAction from "../../../hooks/useAction";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useGenericFilter from "../../../hooks/filters/useGenericFilter";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import AutoCompleteFilter from "../../../components/AutoCompleteFilter";
import baseService from "../../../services/baseService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import TableWithInactiveFilter from "../../../components/table/TableWithInactiveFilter";
import FilterCollapse from "../../../components/FilterCollapse";
import useFilterHandlers from "../../../hooks/useFilterHandlers";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const UsersPage = () => {
  const navigate = useNavigate();
  const { canAccess } = usePermissions();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { get: baseServiceGet, del: baseServiceDel } = useBaseService(navigate);

  // Inicializa filtros e paginação com valores padrões,
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      name: [],
      username: [],
      email: [],
      deleted_at: false,
    },
    "users",
  );

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters);

  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        name: [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear,
    );

  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  const {
    data: usersResponseData,
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers,
    isError: isErrorUsers,
    error: usersQueryError,
  } = useQueryWithLoader({
    queryKey: ["users", filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filters);
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
      };
      const response = await baseServiceGet(entities.users.get, params);
      return response.result || [];
    },
    staleTime: 5 * 60 * 1000,
    select: (data) => {
      return {
        users: data.data.map((user) => ({
          id: user.id,
          name: user.name,
          username: user.username,
          email: user.email,
          status: user.deleted_at ? "Inativo" : "Ativo", // Para exibição na tabela
          isDeleted: !!user.deleted_at, // Booleano para controle de ações
        })),
        totalPages: data.last_page,
      };
    },
  });

  const { users = [], totalPages = 1 } = usersResponseData || {};

  const columns = [
    { key: "name", label: "Nome" },
    { key: "username", label: "Username" },
    { key: "email", label: "E-mail" },
  ];

  useEffect(() => {
    if (!isLoadingUsers && usersResponseData) {
      hideLoader();
    }
    if (isErrorUsers && usersQueryError) {
      hideLoader();
      console.error("Erro ao buscar usuários:", usersQueryError);
      showNotification(
        "error",
        `Erro ao buscar usuários: ${usersQueryError?.message}`,
      );
      navigate("/aluguel/usuarios", { replace: true });
    }
  }, [
    isErrorUsers,
    usersResponseData,
    isLoadingUsers,
    usersQueryError,
    hideLoader,
    showNotification,
    navigate,
  ]);

  useEffect(() => {
    if (location.state?.message) {
      showNotification("error", location.state.message);
    }
  }, [location.state, showNotification]);

  const inputsfilters = useMemo(
    () => [
      {
        label: "Nome",
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase="name"
            model="user"
            value={localFilterValues.name}
            onChange={(selected) => handleFilterChange("name", selected)}
            placeholder="Filtre os usuários pelo nome"
            isMulti
            isClearable
          />
        ),
      },
      {
        label: "Username",
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase="username"
            model="user"
            value={localFilterValues.username}
            onChange={(selected) => handleFilterChange("username", selected)}
            placeholder="Filtre os usuários pelo username"
            isMulti
            isClearable
          />
        ),
      },
      {
        label: "E-mail",
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase="email"
            model="user"
            value={localFilterValues.email}
            onChange={(selected) => handleFilterChange("email", selected)}
            placeholder="Filtre os usuários pelo e-mail"
            isMulti
            isClearable
          />
        ),
      },
    ],
    [localFilterValues, handleFilterChange],
  );

  const inactiveMutation = useMutation({
    mutationFn: async (osDepartamentId) => {
      showLoader();
      const response = await baseServiceDel(
        entities.users.delete(osDepartamentId),
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        queryClient.invalidateQueries({
          queryKey: ["users", filters, pagination],
        });
      }
      hideLoader();
    },
    onError: (error) => {
      console.error("onError ", error);
      hideLoader();
    },
  });

  const activateMutation = inactiveMutation;

  const actions = useMemo(
    () => [
      {
        id: "viewOrganizations",
        icon: faBuilding,
        title: "Organizações do Usuário",
        buttonClass: "btn-success",
        permission: "Listar organizações de usuários",
        onClick: (user) =>
          navigate(`/aluguel/usuarios/organizacoes/${user.id}`),
        show: (user) =>
          !user.isDeleted && canAccess("Listar organizações de usuários"),
      },
      {
        id: "edit",
        icon: faEdit,
        title: "Editar",
        buttonClass: "btn-primary",
        permission: "Atualizar usuários",
        onClick: (user) => navigate(`/aluguel/usuarios/editar/${user.id}`),
        show: (user) => !user.isDeleted && canAccess("Atualizar usuários"),
      },
      {
        id: "delete",
        icon: faTrashAlt,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir usuários",
        onClick: (user) =>
          showConfirmation(
            user,
            "Você tem certeza que deseja excluir?",
            async () => {
              try {
                await inactiveMutation.mutateAsync(user.id);
              } catch (error) {
                console.log("Erro ao excluir usuário:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (user) => !user.isDeleted && canAccess("Excluir usuários"), // Mostra se o usuário NÃO estiver deletado
      },
      {
        id: "activate",
        icon: faUndo,
        title: "Restaurar",
        buttonClass: "btn-info",
        permission: "Excluir usuários",
        onClick: (user) =>
          showConfirmation(
            user,
            "Você tem certeza que deseja restaurar?",
            async () => {
              try {
                await activateMutation.mutateAsync(user.id);
              } catch (error) {
                console.log("Erro ao restaurar usuário:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (user) => user.isDeleted && canAccess("Excluir usuários"), // Mostra se o usuário ESTIVER deletado
      },
    ],
    [activateMutation, canAccess, inactiveMutation, navigate, showConfirmation],
  );

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Usuários" showBackButton={true} backUrl="/dashboard" />

      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <ListHeader
          title="Lista de usuários"
          buttonText="Novo usuário"
          buttonLink="/aluguel/usuarios/criar"
          canAccess={canAccess}
          permission="Criar usuários"
        />

        <TableWithInactiveFilter
          data={users}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setPagination({ page: 1, perPage: newPerPage });
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          isLoading={isLoadingUsers}
          isFetching={isFetchingUsers}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.name : ""}
          text={action.text}
        />
      </div>
    </MainLayout>
  );
};

export default UsersPage;
